/**
 * Generic horizontal scroll-snap carousel controller.
 * Markup contract:
 *   <div data-carousel [data-carousel-auto="true"] [data-carousel-speed="0.4"]>
 *     <div data-carousel-track tabindex="0"> ...items with scroll-snap-align... </div>
 *     <button data-carousel-prev>...</button>
 *     <button data-carousel-next>...</button>
 *     <div data-carousel-dots>...</div>
 *   </div>
 * Used by: Top Categories, Best Sellers / Featured Products, Shop Top Brands, Pet Care Blog.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initCarousel(root) {
    if (!root || root.dataset.carouselInitialized === 'true') return;
    root.dataset.carouselInitialized = 'true';

    var track = root.querySelector('[data-carousel-track]');
    if (!track) return;

    var prevBtn = root.querySelector('[data-carousel-prev]');
    var nextBtn = root.querySelector('[data-carousel-next]');
    var dotsWrap = root.querySelector('[data-carousel-dots]');
    var items = Array.prototype.slice.call(track.children);
    if (items.length === 0) return;

    function step() {
      var item = items[0];
      var style = item.currentStyle || window.getComputedStyle(item);
      var gap = parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || 0) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    function atStart() {
      return track.scrollLeft <= 2;
    }

    function atEnd() {
      return track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    }

    function updateArrows() {
      if (prevBtn) {
        prevBtn.disabled = atStart();
        prevBtn.setAttribute('aria-disabled', atStart() ? 'true' : 'false');
      }
      if (nextBtn) {
        nextBtn.disabled = atEnd();
        nextBtn.setAttribute('aria-disabled', atEnd() ? 'true' : 'false');
      }
      if (dotsWrap) updateDots();
    }

    function scrollByAmount(dir) {
      track.scrollBy({ left: dir * step() * 1.02, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        scrollByAmount(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scrollByAmount(1);
      });
    }

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollByAmount(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollByAmount(-1);
      }
    });

    var dots = [];
    function buildDots() {
      if (!dotsWrap || items.length <= 1) return;
      dotsWrap.innerHTML = '';
      items.forEach(function (_, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'carousel-dot';
        dot.setAttribute('aria-label', 'Go to item ' + (i + 1));
        dot.addEventListener('click', function () {
          items[i].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', inline: 'start', block: 'nearest' });
        });
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });
    }

    function updateDots() {
      if (!dots.length) return;
      var center = track.scrollLeft + track.clientWidth / 2;
      var closest = 0;
      var closestDist = Infinity;
      items.forEach(function (item, i) {
        var itemCenter = item.offsetLeft + item.offsetWidth / 2;
        var dist = Math.abs(itemCenter - center);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === closest);
      });
    }

    buildDots();

    var scrollTicking = false;
    track.addEventListener(
      'scroll',
      function () {
        if (scrollTicking) return;
        scrollTicking = true;
        window.requestAnimationFrame(function () {
          updateArrows();
          scrollTicking = false;
        });
      },
      { passive: true }
    );

    window.addEventListener('resize', updateArrows);
    updateArrows();

    if (root.dataset.carouselAuto === 'true' && items.length > 2 && !reduceMotion) {
      var speed = parseFloat(root.dataset.carouselSpeed) || 0.4;
      var paused = false;
      var rafId = null;

      function tick() {
        if (!paused) {
          if (atEnd()) {
            track.scrollLeft = 0;
          } else {
            track.scrollLeft += speed;
          }
        }
        rafId = window.requestAnimationFrame(tick);
      }

      function pause() {
        paused = true;
      }
      function resume() {
        paused = false;
      }

      root.addEventListener('mouseenter', pause);
      root.addEventListener('mouseleave', resume);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', resume);
      root.addEventListener('touchstart', pause, { passive: true });
      root.addEventListener('touchend', function () {
        window.setTimeout(resume, 1500);
      });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          if (rafId) window.cancelAnimationFrame(rafId);
        } else {
          rafId = window.requestAnimationFrame(tick);
        }
      });

      rafId = window.requestAnimationFrame(tick);
    }
  }

  function initAll() {
    var carousels = document.querySelectorAll('[data-carousel]');
    for (var i = 0; i < carousels.length; i++) {
      initCarousel(carousels[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var carousels = e.target.querySelectorAll('[data-carousel]');
    for (var i = 0; i < carousels.length; i++) {
      initCarousel(carousels[i]);
    }
  });
})();
